
import { useState, useEffect } from 'react';

export default function ValentineBanner() {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    // Set Valentine's Day as the target date
    const valentinesDay = new Date('2025-02-14T23:59:59');
    
    const timer = setInterval(() => {
      const now = new Date();
      const difference = valentinesDay.getTime() - now.getTime();
      
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const scrollToStore = () => {
    const element = document.getElementById('equipment-store');
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/b55d973f1fcdf24807a412442dac853c.jpg"
          alt="Valentine's Day Sale"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-pink-900/80 via-pink-800/70 to-rose-900/80"></div>
      </div>

      {/* Floating Hearts Animation */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-10 text-4xl animate-bounce opacity-60">💕</div>
        <div className="absolute top-20 right-20 text-3xl animate-pulse opacity-50">💝</div>
        <div className="absolute bottom-20 left-1/4 text-2xl animate-bounce opacity-40" style={{ animationDelay: '0.5s' }}>❤️</div>
        <div className="absolute top-1/3 right-1/4 text-3xl animate-pulse opacity-50" style={{ animationDelay: '1s' }}>💖</div>
        <div className="absolute bottom-10 right-10 text-4xl animate-bounce opacity-60" style={{ animationDelay: '0.3s' }}>💗</div>
        <div className="absolute top-1/2 left-10 text-2xl animate-pulse opacity-40" style={{ animationDelay: '0.7s' }}>💘</div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left">
            {/* Sale Badge */}
            <div className="inline-flex items-center gap-2 px-5 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full mb-6">
              <span className="text-2xl">💝</span>
              <span className="text-white font-bold text-sm tracking-wide">LIMITED TIME OFFER</span>
            </div>

            <h2 className="text-5xl lg:text-7xl font-black text-white mb-4 leading-tight">
              Valentine's Day
              <br />
              <span className="bg-gradient-to-r from-pink-300 via-rose-300 to-red-300 bg-clip-text text-transparent">
                MEGA SALE
              </span>
            </h2>

            <p className="text-xl text-pink-100 mb-6 max-w-lg">
              Up to <span className="font-bold text-white text-3xl">35% OFF</span> on premium beauty, accessories, and luxury gifts for your special someone!
            </p>

            {/* Countdown Timer */}
            <div className="mb-8">
              <p className="text-pink-200 text-sm font-semibold mb-3 uppercase tracking-wider">Sale Ends In:</p>
              <div className="flex justify-center lg:justify-start gap-3">
                <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-3 min-w-[70px]">
                  <div className="text-3xl font-black text-white">{timeLeft.days}</div>
                  <div className="text-xs text-pink-200 uppercase">Days</div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-3 min-w-[70px]">
                  <div className="text-3xl font-black text-white">{timeLeft.hours}</div>
                  <div className="text-xs text-pink-200 uppercase">Hours</div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-3 min-w-[70px]">
                  <div className="text-3xl font-black text-white">{timeLeft.minutes}</div>
                  <div className="text-xs text-pink-200 uppercase">Mins</div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-3 min-w-[70px]">
                  <div className="text-3xl font-black text-white">{timeLeft.seconds}</div>
                  <div className="text-xs text-pink-200 uppercase">Secs</div>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-4">
              <button
                onClick={scrollToStore}
                className="group px-8 py-4 bg-white text-pink-600 font-bold rounded-xl hover:bg-pink-50 transition-all shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <span className="flex items-center gap-2">
                  Shop Valentine's Deals
                  <i className="ri-heart-fill text-lg group-hover:scale-125 transition-transform"></i>
                </span>
              </button>
              <button
                onClick={scrollToStore}
                className="px-8 py-4 bg-transparent border-2 border-white text-white font-bold rounded-xl hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
              >
                View All Gifts
              </button>
            </div>
          </div>

          {/* Right Content - Featured Products Preview */}
          <div className="hidden lg:block">
            <div className="relative">
              {/* Main Product Card */}
              <div className="bg-white/10 backdrop-blur-md rounded-3xl p-6 border border-white/20 shadow-2xl">
                <div className="grid grid-cols-2 gap-4">
                  {/* Product 1 */}
                  <div className="bg-white rounded-2xl p-4 hover:scale-105 transition-transform cursor-pointer" onClick={scrollToStore}>
                    <div className="w-full h-32 rounded-xl overflow-hidden mb-3">
                      <img
                        src="https://readdy.ai/api/search-image?query=Charlotte%20Tilbury%20Pillow%20Talk%20lipstick%20set%20in%20elegant%20rose%20gold%20packaging%20with%20matte%20nude%20pink%20lipstick%20and%20lip%20liner%20luxury%20makeup%20gift%20set%20for%20valentines%20day%20premium%20cosmetics%20on%20soft%20pink%20background&width=400&height=400&seq=ct-pillowtalk-set-01&orientation=squarish"
                        alt="Charlotte Tilbury Set"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <p className="text-xs text-gray-500 font-semibold">Charlotte Tilbury</p>
                    <p className="text-sm font-bold text-gray-900 truncate">Pillow Talk Set</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-pink-600 font-bold">$49.99</span>
                      <span className="text-xs text-gray-400 line-through">$75</span>
                    </div>
                  </div>

                  {/* Product 2 */}
                  <div className="bg-white rounded-2xl p-4 hover:scale-105 transition-transform cursor-pointer" onClick={scrollToStore}>
                    <div className="w-full h-32 rounded-xl overflow-hidden mb-3">
                      <img
                        src="https://readdy.ai/api/search-image?query=Pandora%20sterling%20silver%20charm%20bracelet%20with%20heart%20shaped%20charms%20and%20rose%20gold%20accents%20romantic%20jewelry%20gift%20set%20for%20valentines%20day%20elegant%20accessories%20on%20soft%20pink%20background&width=400&height=400&seq=pandora-heart-set-01&orientation=squarish"
                        alt="Pandora Bracelet"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <p className="text-xs text-gray-500 font-semibold">Pandora</p>
                    <p className="text-sm font-bold text-gray-900 truncate">Heart Bracelet</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-pink-600 font-bold">$129.99</span>
                      <span className="text-xs text-gray-400 line-through">$175</span>
                    </div>
                  </div>

                  {/* Product 3 */}
                  <div className="bg-white rounded-2xl p-4 hover:scale-105 transition-transform cursor-pointer" onClick={scrollToStore}>
                    <div className="w-full h-32 rounded-xl overflow-hidden mb-3">
                      <img
                        src="https://readdy.ai/api/search-image?query=YSL%20Libre%20eau%20de%20parfum%20gift%20set%20with%20full%20size%20bottle%20and%20travel%20spray%20in%20elegant%20black%20and%20gold%20packaging%20luxury%20womens%20fragrance%20for%20valentines%20premium%20perfume%20on%20white%20background&width=400&height=400&seq=ysl-libre-set-01&orientation=squarish"
                        alt="YSL Libre"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <p className="text-xs text-gray-500 font-semibold">YSL</p>
                    <p className="text-sm font-bold text-gray-900 truncate">Libre Perfume Set</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-pink-600 font-bold">$124.99</span>
                      <span className="text-xs text-gray-400 line-through">$168</span>
                    </div>
                  </div>

                  {/* Product 4 */}
                  <div className="bg-white rounded-2xl p-4 hover:scale-105 transition-transform cursor-pointer" onClick={scrollToStore}>
                    <div className="w-full h-32 rounded-xl overflow-hidden mb-3">
                      <img
                        src="https://readdy.ai/api/search-image?query=Dyson%20Airwrap%20complete%20styling%20set%20with%20multiple%20attachments%20in%20fuchsia%20and%20nickel%20premium%20hair%20styling%20tool%20gift%20luxury%20beauty%20device%20on%20white%20background&width=400&height=400&seq=dyson-airwrap-03&orientation=squarish"
                        alt="Dyson Airwrap"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <p className="text-xs text-gray-500 font-semibold">Dyson</p>
                    <p className="text-sm font-bold text-gray-900 truncate">Airwrap Complete</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-pink-600 font-bold">$499.99</span>
                      <span className="text-xs text-gray-400 line-through">$599</span>
                    </div>
                  </div>
                </div>

                {/* Trending Badge */}
                <div className="absolute -top-4 -right-4 bg-gradient-to-r from-pink-500 to-rose-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg animate-pulse">
                  🔥 TRENDING
                </div>
              </div>

              {/* Decorative Elements */}
              <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-pink-400/30 rounded-full blur-2xl"></div>
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-rose-400/30 rounded-full blur-2xl"></div>
            </div>
          </div>
        </div>

        {/* Bottom Stats Bar */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 text-center border border-white/20">
            <div className="text-3xl font-black text-white">25+</div>
            <div className="text-sm text-pink-200">Valentine Products</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 text-center border border-white/20">
            <div className="text-3xl font-black text-white">35%</div>
            <div className="text-sm text-pink-200">Max Savings</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 text-center border border-white/20">
            <div className="text-3xl font-black text-white">Free</div>
            <div className="text-sm text-pink-200">Gift Wrapping</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 text-center border border-white/20">
            <div className="text-3xl font-black text-white">24hr</div>
            <div className="text-sm text-pink-200">Fast Shipping</div>
          </div>
        </div>
      </div>
    </section>
  );
}
